import { gql, type TypedDocumentNode } from '@apollo/client';

import { Navigate } from '@sorare/routing';
import {
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_BOARDS_PRO_BOARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useEnableSorare27Nav } from '@sorare/core/src/hooks/useEnableSorare27Nav.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';
import { useTargetSo5CompetitionSearchParam } from '#src/hooks/useTargetSo5CompetitionSearchParam.ts';

import type {
  ProCareerPageQuery,
  ProCareerPageQueryVariables,
} from './__generated__/page.graphql.ts';

// oxlint-disable-next-line sorare/no-unnormalized-object-in-array -- pre-existing, unrelated to this change
const PRO_CAREER_PAGE_QUERY = gql`
  query ProCareerPageQuery($sport: Sport!) {
    currentUser {
      slug
      boards(
        mode: CAREER
        sport: $sport
        rarities: [limited, rare, super_rare, unique]
      ) {
        id
        title
        ... on CareerBoard {
          id
          so5Competition {
            slug
          }
        }
      }
    }
  }
` as TypedDocumentNode<ProCareerPageQuery, ProCareerPageQueryVariables>;

export const loader = queryLoader(PRO_CAREER_PAGE_QUERY, ({ sport }) => ({
  sport,
}));

const ProCareerPage = () => {
  const { generateSportContextPath, sport } = useSportContext();
  const enableSorare27Nav = useEnableSorare27Nav();
  const {
    flags: {
      enableProCareerMode = {
        baseball: false,
        football: false,
        nba: false,
      },
    },
  } = useFeatureFlags();
  const { data } = useQueryLoader<ProCareerPageQuery>();
  const { getProView } = useLastVisitedBoardView();
  const targetSo5Competition = useTargetSo5CompetitionSearchParam();

  if (!enableProCareerMode[sport.toLowerCase()] && !enableSorare27Nav) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_PLAY)}
        replace
        keepSearchParams
      />
    );
  }

  const boards = data?.currentUser?.boards ?? [];
  const lastBoardTitle = getProView().lastVisitedBoardTitle;
  const boardForCompetition = targetSo5Competition
    ? boards.find(
        b =>
          isType(b, 'CareerBoard') &&
          b.so5Competition?.slug === targetSo5Competition
      )
    : undefined;
  const boardForLastTitle = lastBoardTitle
    ? boards.find(b => b.title === lastBoardTitle)
    : undefined;
  const boardToNavigate = boardForCompetition ?? boardForLastTitle ?? boards[0];

  if (boardToNavigate) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO_BOARD, {
          params: { board: idFromObject(boardToNavigate.id) },
        })}
        replace
        keepSearchParams
      />
    );
  }

  return null;
};
export default ProCareerPage;
